import {
  IFareRedemptionStub,
  IRequestAccessibilityFeature,
  IRequestRiderType,
  RequestIntentType,
} from '@sparelabs/domain-logic'
import { IPoint } from '@sparelabs/geography'
import { IEstimateOutput } from './EstimateOperationTypes'
import { CreatedInterface } from './EstimateTypes'
import { IPrice } from './SharedTypes'

/**
 * TODO: create a new lib/multimodal-client and move this there
 */

export interface IJourneyOptionsQueryParams {
  createdInterface: CreatedInterface
  ts: number
  arriveBy: boolean
  startLongitude: number
  startLatitude: number
  endLongitude: number
  endLatitude: number
  legModes?: JourneyOptionLegMode[]
  riderId?: string
  accessibilityFeatures: IRequestAccessibilityFeature[]
  numRiders: number
  riders: IRequestRiderType[]
  paymentMethodTypeId?: string
  /**
   * @deprecated What is this? Can we remove it?
   */
  overwriteOrganizationId?: string
  accessModes?: OpenTripPlannerAccessMode[]
  egressModes?: OpenTripPlannerAccessMode[]
}

export interface IJourneyOptionsRawQueryParams
  extends Omit<IJourneyOptionsQueryParams, 'riders' | 'accessibilityFeatures'> {
  'riders[]'?: string[]
  'accessibilityFeatures[]'?: string[]
}

export interface IJourneyOptionsRequestQueryParams
  extends Omit<IJourneyOptionsQueryParams, 'riders' | 'accessibilityFeatures'> {
  riders?: string[]
  accessibilityFeatures?: string[]
}

export enum JourneyOptionLegMode {
  OnDemand = 'onDemand',
  Bus = 'bus',
  Train = 'train',
  Walk = 'walk',
  Tram = 'tram',
  Bicycle = 'bicycle',
}

export interface IJourneyOptionsResponse {
  data: IJourneyOption[]
}

export interface IJourneyOption {
  /* Meta data */
  riderId?: string
  createdInterface: CreatedInterface

  /* Geographical data */
  requestedStartAddress?: string
  requestedStartLocationName?: string
  requestedStartLocation: IPoint
  requestedEndAddress?: string
  requestedEndLocationName?: string
  requestedEndLocation: IPoint

  /* Temporal data */
  intentType: RequestIntentType
  scheduledStartTs: number
  scheduledMinStartTs?: number
  scheduledMaxStartTs?: number
  scheduledEndTs: number
  scheduledMinEndTs?: number
  scheduledMaxEndTs?: number

  /* Fare data */
  fare?: IPrice // null unless there is an on-demand leg
  fareRedemptions: IFareRedemptionStub[]

  riders: IRequestRiderType[]
  accessibilityFeatures: IRequestAccessibilityFeature[]
  legs: IJourneyOptionLeg[]

  /**
   * False if we failed to get an estimate from Spare for an on-demand leg that is part of this journey option
   */
  isFeasible: boolean

  /**
   * How much time is spent waiting for transit to arrive, in seconds.
   */
  waitingTimeSeconds: number
  /**
   * How far the rider has to walk, in meters.
   */
  walkDistanceMeters: number
  /**
   * How much time is spent walking, in seconds.
   */
  walkTimeSeconds: number
}

export interface IJourneyOptionLeg {
  /* Agency data */
  agencyOtpId?: string
  agencyGtfsId?: string

  /* Route data */
  routeOtpId?: string
  routeGtfsId?: string
  routeLongName?: string
  routeShortName?: string
  routeColor?: string
  routeTextColor?: string

  mode: JourneyOptionLegMode
  interlineWithPreviousLeg?: boolean

  /**
   * Note! OpenTripPlanner trip IDs are different than the trip IDs in the source GTFS data.
   *
   * @deprecated Right now we don't use the tripOtpId anywhere, you probably meant to use the tripGtfsId!
   */
  tripOtpId?: string
  tripGtfsId?: string
  headsign?: string
  directionId?: string

  /* Geographical data */
  travelDistanceM: number
  polyline?: string
  pickupAddress?: string
  pickupLocation?: IPoint
  dropoffAddress?: string
  dropoffLocation?: IPoint

  /* Temporal data */
  duration: number
  startTs: number
  startMinTs?: number
  startMaxTs?: number
  endTs: number
  endMinTs?: number
  endMaxTs?: number

  nextDepartures: IJourneyOptionLegNextDeparture[]
  steps: IJourneyOptionStep[]

  pickupType: PickupDropoffType
  dropoffType: PickupDropoffType

  agencyPhoneNumber?: string

  /**
   * The estimate from Spare for this on-demand leg.
   * Only set for On-Demand legs
   */
  estimate?: IEstimateOutput

  alerts: IJourneyOptionLegAlert[]
}

export enum JourneyAlertCause {
  Accident = 'ACCIDENT',
  Construction = 'CONSTRUCTION',
  Demonstration = 'DEMONSTRATION',
  Holiday = 'HOLIDAY',
  Maintenance = 'MAINTENANCE',
  MedicalEmergency = 'MEDICAL_EMERGENCY',
  OtherCause = 'OTHER_CAUSE',
  PoliceActivity = 'POLICE_ACTIVITY',
  Strike = 'STRIKE',
  TechnicalProblem = 'TECHNICAL_PROBLEM',
  UnknownCause = 'UNKNOWN_CAUSE',
  Weather = 'WEATHER',
}

export enum JourneyAlertSeverityLevel {
  Info = 'INFO',
  Severe = 'SEVERE',
  Unknown = 'UNKNOWN_SEVERITY',
  Warning = 'WARNING',
}

export enum JourneyAlertEffect {
  AccessibilityIssue = 'ACCESSIBILITY_ISSUE',
  AdditionalService = 'ADDITIONAL_SERVICE',
  Detour = 'DETOUR',
  ModifiedService = 'MODIFIED_SERVICE',
  NoEffect = 'NO_EFFECT',
  NoService = 'NO_SERVICE',
  OtherEffect = 'OTHER_EFFECT',
  ReducedService = 'REDUCED_SERVICE',
  SignificantDelays = 'SIGNIFICANT_DELAYS',
  StopMoved = 'STOP_MOVED',
  UnknownEffect = 'UNKNOWN_EFFECT',
}

export interface IJourneyOptionLegAlert {
  id?: string | null
  cause?: JourneyAlertCause | null
  effect?: JourneyAlertEffect | null
  descriptionText?: string | null
  headerText?: string | null
  url?: string | null
  severityLevel?: JourneyAlertSeverityLevel | null
  effectiveStartDate?: number | null
  effectiveEndDate?: number | null
}

export interface IJourneyOptionLegNextDeparture {
  ts: number
  frequencyInSec?: number
  isLast?: boolean
}

export interface IJourneyOptionStep {
  name: string
  location: IPoint
  gtfsId?: string | null
}

export enum PickupDropoffType {
  Scheduled = 'SCHEDULED', // Regularly scheduled pickup / drop off.
  CoordinateWithDriver = 'COORDINATE_WITH_DRIVER', // Must coordinate with driver to arrange pickup / drop off.
  CallAgency = 'CALL_AGENCY', // Must phone agency to arrange pickup / drop off.
  None = 'NONE', // No pickup / drop off available.
}

// docs.opentripplanner.org/api/dev-2.x/graphql-gtfs/types/PlanAccessMode
export enum OpenTripPlannerAccessMode {
  BICYCLE = 'BICYCLE',
  BICYCLE_PARKING = 'BICYCLE_PARKING',
  BICYCLE_RENTAL = 'BICYCLE_RENTAL',
  CAR = 'CAR',
  CAR_DROP_OFF = 'CAR_DROP_OFF',
  CAR_PARKING = 'CAR_PARKING',
  CAR_RENTAL = 'CAR_RENTAL',
  FLEX = 'FLEX',
  SCOOTER_RENTAL = 'SCOOTER_RENTAL',
  WALK = 'WALK',
}

// docs.opentripplanner.org/api/dev-2.x/graphql-gtfs/types/PlanTransitModesInput
export enum OpenTripPlannerTransitMode {
  AIRPLANE = 'AIRPLANE',
  BUS = 'BUS',
  CABLE_CAR = 'CABLE_CAR',
  CARPOOL = 'CARPOOL',
  COACH = 'COACH',
  FERRY = 'FERRY',
  FUNICULAR = 'FUNICULAR',
  GONDOLA = 'GONDOLA',
  MONORAIL = 'MONORAIL',
  RAIL = 'RAIL',
  SUBWAY = 'SUBWAY',
  TAXI = 'TAXI',
  TRAM = 'TRAM',
  TROLLEYBUS = 'TROLLEYBUS',
}
